import type { IRetryPolicy, RetryContext } from "@microsoft/signalr";

/**
 * The MVP's production-proven reconnect backoff (verbatim): 1s → 2 → 4 → 8 → 16 → 30s cap, then
 * hold at 30s forever (never give up). The policy owns its OWN attempt counter and ignores SignalR's
 * `previousRetryCount` (which resets to 0 on every successful reconnect) so it can implement the storm
 * guard: the RealtimeClient resets this counter ONLY after a connection has held stable > 15s (the
 * reconnect-storm the MVP documented). A brief reconnect-then-drop (a flap) does
 * NOT reset — the backoff keeps climbing toward the 30s cap instead of restarting at 1s.
 */
export const BACKOFF_MS: readonly number[] = [1000, 2000, 4000, 8000, 16000, 30000];

export class CntrlRetryPolicy implements IRetryPolicy {
  private attempt = 0;

  nextRetryDelayInMilliseconds(_retryContext: RetryContext): number {
    const i = Math.min(this.attempt, BACKOFF_MS.length - 1);
    this.attempt += 1;
    return BACKOFF_MS[i];
  }

  /** Reset the backoff to 1s. Called by the client only after a connection held stable > 15s (guard).*/
  reset(): void {
    this.attempt = 0;
  }

  /** The current attempt index (for tests/telemetry). */
  get currentAttempt(): number {
    return this.attempt;
  }
}
